import { useMemo } from 'react';
import { Device, Range, rangeToDate, Trigger } from '@/hooks/useTriggerStream';
import { format } from 'date-fns';
import { buildDeviceColorMap, colorForDevice } from './vizPalette';
import { useT } from '@/i18n/LanguageContext';
import { visualizeContent } from '@/i18n/content/visualize';

interface Props {
  triggers: Trigger[];
  devices: Device[];
  range: Range;
  dark: boolean;
  colorMap?: Map<string, string>;
  showLabels?: boolean;
  showLegend?: boolean;
}

// Symmetric stacked stream graph with smooth alluvial-style bands.
export function FlowRiver({ triggers, devices, range, dark, colorMap, showLabels = false, showLegend = true }: Props) {
  const t = useT(visualizeContent).charts;
  const W = 1200;
  const H = 380;

  // Fine buckets: the bands stay close to the real, irregular pulse counts
  // instead of being averaged into broad gentle waves.
  const bucketCount = range === '1h' ? 180 : range === '24h' ? 240 : range === '7d' ? 280 : range === '30d' ? 320 : range === '1y' ? 340 : 360;
  // Smoothed pulses per bucket that already fill the frame — keeps modest
  // activity clearly visible instead of a near-flat stream.
  const AMPLITUDE_FLOOR = 2;

  const { stacks, deviceOrder, maxTotal, timeLabels } = useMemo(() => {
    if (!triggers.length) return { stacks: [], deviceOrder: [], maxTotal: 0, timeLabels: [] as string[] };

    // Keep each chart anchored to its requested calendar window. Deriving these
    // bounds from the data would make sparse week/year/decade charts appear to
    // span only the interval between their first and last trigger.
    const tMin = rangeToDate(range).getTime();
    const tMax = Date.now();
    const span = Math.max(1, tMax - tMin);
    const bucketMs = span / bucketCount;

    const counts = new Map<string, number[]>(); // deviceId -> per-bucket count
    triggers.forEach((t) => {
      const timestamp = new Date(t.created_at).getTime();
      if (!Number.isFinite(timestamp) || timestamp < tMin || timestamp > tMax) return;
      const idx = Math.max(0, Math.min(bucketCount - 1, Math.floor((timestamp - tMin) / bucketMs)));
      const existing = counts.get(t.device_id);
      if (existing) {
        existing[idx] += 1;
      } else {
        const values = Array(bucketCount).fill(0);
        values[idx] = 1;
        counts.set(t.device_id, values);
      }
    });

    // Minimal single-pass smoothing (radius 1, weights 1-2-1): softens only
    // the harshest needle spikes while preserving the jagged, erratic
    // character of the real trigger data.
    const smoothSeries = (arr: number[]) =>
      arr.map((v, i) => {
        const prev = arr[i - 1] ?? v;
        const next = arr[i + 1] ?? v;
        return (prev + v * 2 + next) / 4;
      });
    counts.forEach((arr, id) => counts.set(id, smoothSeries(arr)));

    // Inside-out ordering: the busiest streams sit around the centre line and
    // quieter ones fan out symmetrically, like the reference streamgraph.
    const byTotal = Array.from(counts.entries())
      .map(([id, arr]) => ({ id, total: arr.reduce((a, b) => a + b, 0) }))
      .sort((a, b) => b.total - a.total)
      .map((d) => d.id);
    const front: string[] = [];
    const back: string[] = [];
    byTotal.forEach((id, i) => (i % 2 === 0 ? front.push(id) : back.unshift(id)));
    const order = [...back, ...front];

    const totals = Array(bucketCount).fill(0);
    counts.forEach((arr) => arr.forEach((v, i) => (totals[i] += v)));

    // Outlier damping: the 98th percentile of bucket totals sets the scale
    // reference; spikes above it are square-root compressed so they stay
    // visible without flattening the rest of the river.
    const sortedTotals = [...totals].sort((a, b) => a - b);
    const p98 =
      sortedTotals[
        Math.min(sortedTotals.length - 1, Math.max(0, Math.ceil(sortedTotals.length * 0.98) - 1))
      ] || 0;
    const reference = Math.max(p98, AMPLITUDE_FLOOR);
    const compressedTotals = totals.map((total) =>
      total <= reference ? total : reference + Math.sqrt((total - reference) * reference),
    );
    counts.forEach((arr, id) => {
      counts.set(id, arr.map((v, i) => (totals[i] > 0 ? (v * compressedTotals[i]) / totals[i] : v)));
    });
    const maxT = Math.max(1, ...compressedTotals);

    const labels = Array.from({ length: 6 }, (_, i) => {
      const ts = tMin + (span * i) / 5;
      const labelFormat = range === '1h' || range === '24h'
        ? 'HH:mm'
        : range === '1y'
          ? 'MMM yyyy'
          : range === '10y'
            ? 'yyyy'
            : 'MMM dd';
      return format(new Date(ts), labelFormat);
    });

    const stackData = order.flatMap((id) => {
      const values = counts.get(id);
      return values ? [{ id, values }] : [];
    });

    return { stacks: stackData, deviceOrder: order, maxTotal: maxT, timeLabels: labels };
  }, [triggers, bucketCount, range]);


  const text = 'rgb(var(--site-fg) / 0.5)';
  const baseline = H / 2;
  // The highest (outlier-damped) peak spans ~92% of the chart height.
  const yScale = (H * 0.92) / Math.max(maxTotal, AMPLITUDE_FLOOR);
  const sortedDeviceIds = [...deviceOrder].sort();
  const deviceLabel = (deviceId: string) => {
    const configuredName = devices.find((device) => device.device_id === deviceId)?.name?.trim();
    if (configuredName) return configuredName;
    const index = sortedDeviceIds.indexOf(deviceId);
    return t.sculptureFallback(String(index + 1).padStart(2, '0'));
  };
  const localColorMap = useMemo(
    () => colorMap ?? buildDeviceColorMap([...devices.map((d) => d.device_id), ...deviceOrder]),
    [colorMap, devices, deviceOrder],
  );
  const deviceColor = (deviceId: string) => localColorMap.get(deviceId) ?? colorForDevice(deviceId);

  // Every known device stays in the legend, even when it produced nothing in
  // this window — silent devices are dimmed rather than dropped.
  const legendIds = useMemo(() => {
    const active = new Set(deviceOrder);
    const idle = devices
      .map((d) => d.device_id)
      .filter((id) => id && !active.has(id))
      .sort();
    return [...deviceOrder, ...Array.from(new Set(idle))];
  }, [deviceOrder, devices]);


  // Catmull-Rom -> bezier: continuous tangents, so the outline flows instead of
  // stepping between buckets.
  const smoothLine = (points: Array<{ x: number; y: number }>): string => {
    if (!points.length) return '';
    if (points.length < 3) return points.map((p, i) => `${i ? 'L' : 'M'}${p.x},${p.y}`).join(' ');
    let d = `M${points[0].x},${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(0, i - 1)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(points.length - 1, i + 2)];
      const c1x = p1.x + (p2.x - p0.x) / 6;
      const c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6;
      const c2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`;
    }
    return d;
  };

  const buildPath = (idx: number): string => {
    const N = bucketCount;
    const xStep = W / (N - 1);
    const topPoints: Array<{ x: number; y: number }> = [];
    const bottomPoints: Array<{ x: number; y: number }> = [];

    for (let i = 0; i < N; i++) {
      const x = i * xStep;
      const total = stacks.reduce((sum, stack) => sum + stack.values[i], 0);
      const lower = stacks.slice(0, idx).reduce((sum, stack) => sum + stack.values[i], 0);
      const value = stacks[idx]?.values[i] ?? 0;
      const streamTop = baseline - total * yScale / 2;
      topPoints.push({ x, y: streamTop + lower * yScale });
      bottomPoints.push({ x, y: streamTop + (lower + value) * yScale });
    }

    return `${smoothLine(topPoints)} ${smoothLine(bottomPoints.reverse()).replace(/^M/, 'L')} Z`;
  };

  return (
    <figure className="w-full" aria-label={t.riverAria}>
      <svg viewBox={`0 0 ${W} ${H + 30}`} className="w-full h-auto">
        <g>
          {stacks.map((s, idx) => (
            <path
              key={s.id}
              d={buildPath(idx)}
              fill={deviceColor(s.id)}
              opacity={0.94}
              stroke="var(--viz-embed-light)"
              strokeWidth={0.5}
              strokeOpacity={0.35}
              strokeLinejoin="round"
            />
          ))}
        </g>

        {/* Baseline */}
        <line x1={0} y1={baseline} x2={W} y2={baseline} stroke={text} strokeWidth={0.3} opacity={0.25} />



        {/* Time labels */}
        {timeLabels.map((lbl, i) => (
          <text
            key={i}
            x={(W * i) / (timeLabels.length - 1)}
            y={H + 22}
            textAnchor={i === 0 ? 'start' : i === timeLabels.length - 1 ? 'end' : 'middle'}
            fontSize={10}
            fontFamily="ui-monospace, monospace"
            fill={text}
          >
            {lbl}
          </text>
        ))}

        {stacks.length === 0 && (
          <text x={W / 2} y={H / 2} textAnchor="middle" fontSize={14} fontFamily="ui-monospace, monospace" fill={text}>
            {t.noActivity}
          </text>
        )}
      </svg>

      {showLegend && legendIds.length > 0 && (
        <figcaption className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 px-2 font-mono text-[10px] uppercase text-sitefg/55">
          {legendIds.map((deviceId) => {
            const silent = !deviceOrder.includes(deviceId);
            return (
              <span
                key={deviceId}
                className={`inline-flex items-center gap-2 whitespace-nowrap ${silent ? 'opacity-35' : ''}`}
                title={silent ? t.noActivity : undefined}
              >
                <svg viewBox="0 0 10 10" className="h-2.5 w-2.5" aria-hidden="true">
                  <circle
                    cx="5"
                    cy="5"
                    r={silent ? 4 : 5}
                    fill={silent ? 'none' : deviceColor(deviceId)}
                    stroke={deviceColor(deviceId)}
                    strokeWidth={silent ? 2 : 0}
                  />
                </svg>
                {showLabels ? deviceLabel(deviceId) : null}
              </span>
            );
          })}
        </figcaption>
      )}
    </figure>
  );
}
